import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XCircleIcon, HeartIcon } from '@heroicons/react/24/outline';
import { subscriptionAPI, DeclineSubscriptionRequest } from '../../services/api';

interface TrialExpiredModalProps {
  isOpen: boolean;
  onSubscribe: () => void;
  onDecline: () => void;
}

const DECLINE_REASONS = [
  { id: 'too_expensive', label: 'Too expensive for my budget' },
  { id: 'not_enough_features', label: 'Not enough features I need' },
  { id: 'temporary_need', label: 'Only needed it temporarily' },
  { id: 'found_alternative', label: 'Found a better alternative' },
  { id: 'technical_issues', label: 'Technical issues or bugs' },
  { id: 'content_not_appropriate', label: 'Content not age-appropriate' },
  { id: 'other', label: 'Other reason' }
];

const TrialExpiredModal: React.FC<TrialExpiredModalProps> = ({
  isOpen,
  onSubscribe,
  onDecline
}) => {
  const [showReasonForm, setShowReasonForm] = useState(false);
  const [selectedReason, setSelectedReason] = useState('');
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);

  const handleDeclineClick = () => {
    setShowReasonForm(true);
  };

  const handleSubmitDecline = async () => {
    if (!selectedReason) return;

    setIsSubmitting(true);
    try {
      const data: DeclineSubscriptionRequest = {
        reason: DECLINE_REASONS.find(r => r.id === selectedReason)?.label || selectedReason,
        feedback: feedback || undefined
      };

      await subscriptionAPI.decline(data);
      setShowThankYou(true);

      // Close after 2 seconds
      setTimeout(() => {
        onDecline();
      }, 2000);
    } catch (err) {
      console.error('Error submitting decline reason:', err);
      // Still allow them to close
      onDecline();
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  if (showThankYou) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-2xl"
        >
          <HeartIcon className="w-16 h-16 text-purple-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Thank You!</h2>
          <p className="text-gray-600">
            We appreciate your feedback. We hope to see you again in the future!
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden"
        >
          {!showReasonForm ? (
            <>
              {/* Main Expired Message */}
              <div className="bg-gradient-to-r from-red-50 to-orange-50 border-b border-red-200 p-8 text-center">
                <XCircleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <h2 className="text-3xl font-bold text-gray-800 mb-2">
                  Your Free Trial Has Ended
                </h2>
                <p className="text-gray-600 text-lg">
                  Choose a subscription plan to continue your learning journey with Knoozy
                </p>
              </div>

              {/* Content */}
              <div className="p-8 space-y-6">
                {/* What You'll Miss */}
                <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
                  <h3 className="font-bold text-gray-800 mb-4 text-center">
                    Without a subscription, you'll miss out on:
                  </h3>
                  <ul className="space-y-3">
                    {[
                      'Daily current affairs tailored to your age group',
                      'Interactive quizzes and learning activities',
                      'Progress tracking and achievements',
                      'Safe, ad-free learning environment',
                      'Content from trusted sources worldwide'
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-start space-x-3">
                        <span className="text-red-500 text-xl">✗</span>
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Social Proof */}
                <div className="text-center py-4">
                  <p className="text-gray-600 mb-2">
                    <strong className="text-indigo-600">2,000+ students</strong> from{' '}
                    <strong className="text-indigo-600">50+ schools</strong> are already learning with Knoozy
                  </p>
                  <div className="flex justify-center space-x-1 text-yellow-400 text-2xl">
                    ★★★★★
                  </div>
                </div>

                {/* CTA Buttons */}
                <div className="space-y-3">
                  <button
                    onClick={onSubscribe}
                    className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-4 px-6 rounded-xl font-bold text-lg hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  >
                    Choose a Subscription Plan
                  </button>

                  <button
                    onClick={handleDeclineClick}
                    className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-xl font-medium hover:bg-gray-200 transition-colors"
                  >
                    I Don't Want to Subscribe
                  </button>
                </div>

                <p className="text-xs text-center text-gray-500">
                  Your school administrator will help you complete the subscription process
                </p>
              </div>
            </>
          ) : (
            <>
              {/* Reason Collection Form */}
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border-b border-purple-200 p-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">
                  We'd Love Your Feedback
                </h2>
                <p className="text-gray-600">
                  Help us improve by letting us know why you're not subscribing
                </p>
              </div>

              <div className="p-6 space-y-6">
                {/* Reason Selection */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    What's the main reason? *
                  </label>
                  <div className="space-y-2">
                    {DECLINE_REASONS.map((reason) => (
                      <label
                        key={reason.id}
                        className={`flex items-center p-3 border-2 rounded-xl cursor-pointer transition-all ${
                          selectedReason === reason.id
                            ? 'border-indigo-600 bg-indigo-50'
                            : 'border-gray-200 hover:border-indigo-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="reason"
                          value={reason.id}
                          checked={selectedReason === reason.id}
                          onChange={(e) => setSelectedReason(e.target.value)}
                          className="w-4 h-4 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="ml-3 text-gray-700">{reason.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Optional Feedback */}
                {selectedReason && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="overflow-hidden"
                  >
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Any additional feedback? (Optional)
                    </label>
                    <textarea
                      value={feedback}
                      onChange={(e) => setFeedback(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                      rows={3}
                      placeholder="Tell us more about your experience..."
                    />
                  </motion.div>
                )}

                {/* Action Buttons */}
                <div className="flex space-x-3 pt-4">
                  <button
                    onClick={() => setShowReasonForm(false)}
                    className="flex-1 bg-gray-100 text-gray-700 py-3 px-4 rounded-xl font-medium hover:bg-gray-200 transition-colors"
                    disabled={isSubmitting}
                  >
                    Back
                  </button>
                  <button
                    onClick={handleSubmitDecline}
                    disabled={!selectedReason || isSubmitting}
                    className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 px-4 rounded-xl font-semibold hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
                  </button>
                </div>

                <p className="text-xs text-center text-gray-500">
                  Your feedback helps us improve Knoozy for everyone
                </p>
              </div>
            </>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default TrialExpiredModal;
